import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api, { authHttp } from "@/api/client";
import type { User, AppConfig } from "@/types";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const config = ref<AppConfig | null>(null);
  const loading = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(() => user.value?.is_admin ?? false);
  const isVantiva = computed(() => user.value?.is_vantiva ?? false);
  const isLocalInterface = computed(
    () => config.value?.isLocalInterface ?? false,
  );

  async function fetchSession() {
    loading.value = true;
    try {
      const { data } = await api.get("/auth/session");
      user.value = data.user;
      config.value = data.config;
    } catch {
      user.value = null;
      config.value = null;
    } finally {
      loading.value = false;
    }
  }

  async function login(
    email: string,
    password: string,
  ): Promise<{ totp_required: boolean; message?: string }> {

    const { data } = await authHttp.post("/login", { email, password });
    return data;
  }

  async function verifyTotp(
    code: string,
  ): Promise<{ success: boolean; message?: string }> {
    const { data } = await authHttp.post("/verify-totp", { totp: code });
    if (data.message === "Connected") {
      await fetchSession();
      return { success: true, message: data.message };
    }
    return { success: false, message: data.error };
  }

  async function logout() {
    window.location.href = "/logout";
  }

  async function changePassword(
    previous: string,
    password: string,
    confirm: string,
  ) {
    const { data } = await api.post("/change-password", {
      previous,
      password,
      confirm,
    });
    return data;
  }

  async function generateApiKey(): Promise<{
    success: boolean;
    api_key?: string;
    message: string;
  }> {
    const { data } = await authHttp.post("/generate_api_key", {});
    if (data.success && user.value) {
      user.value.api_key = data.api_key;
    }
    return data;
  }

  async function deleteApiKey(): Promise<{
    success: boolean;
    message: string;
  }> {
    const { data } = await authHttp.post("/delete_api_key", {});
    if (data.success && user.value) {
      user.value.api_key = null;
    }
    return data;
  }

  return {
    user,
    config,
    loading,
    isAuthenticated,
    isAdmin,
    isVantiva,
    isLocalInterface,
    fetchSession,
    login,
    verifyTotp,
    logout,
    changePassword,
    generateApiKey,
    deleteApiKey,
  };
});
